import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Context,
  type ReactNode,
} from 'react';
import * as authApi from '@/api/auth';
import { clearAuth, setAuthToken, setTenantHeader, setUnauthorizedHandler } from '@/api/client';
import type { AccountPersona, OAuthProvider, UserInfoResponse } from '@/api/types';
import { identifyTenant, resetIdentity, trackEvent } from '@/lib/analytics';
import {
  clearPortalSession,
  persistPortalSession,
  restorePortalSession,
} from './portalSessionStorage';
import { tokenStorage } from './tokenStorage';

export interface AuthState {
  token: string | null;
  user: UserInfoResponse | null;
  tenantId: string | null;
}

/**
 * Resultado del flujo OAuth: además del user, devolvemos `isNewUser` para que la UI sepa si
 * tiene que mandar a onboarding (primer signup) o directamente al dashboard.
 */
export interface OAuthSignInOutcome {
  user: UserInfoResponse;
  isNewUser: boolean;
}

export interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  login: (email: string, password: string, tenantId?: string) => Promise<UserInfoResponse>;
  /**
   * Slice I — login con un id_token de un IdP externo. Caller (componente del botón) obtiene el
   * idToken del SDK del provider y lo pasa acá.
   */
  signInWithProvider: (
    provider: OAuthProvider,
    idToken: string,
    persona?: AccountPersona,
  ) => Promise<OAuthSignInOutcome>;
  /**
   * Canjea un token de magic link (login passwordless). Setea la sesión igual que `login` y
   * devuelve el user para el redirect por rol.
   */
  redeemMagicLink: (token: string) => Promise<UserInfoResponse>;
  logout: () => void;
}

interface AuthContextGlobal {
  /** Stable context identity across Vite HMR/remounts in the same browser window. */
  tildeAuthContext?: Context<AuthContextValue | null>;
}

// During Vite HMR, App/AuthContext and a lazy route can briefly be evaluated as separate module
// instances. If each instance creates its own React context, the provider is present in the tree
// but useAuth() reads a different context and throws. Reusing one context on globalThis keeps the
// provider/consumer contract stable. La persistencia tab-scoped de la sesión vive separada en
// portalSessionStorage.ts y sólo se hidrata al montar el provider.
const authContextGlobal = globalThis as typeof globalThis & AuthContextGlobal;
export const AuthContext =
  authContextGlobal.tildeAuthContext ??
  (authContextGlobal.tildeAuthContext = createContext<AuthContextValue | null>(null));

interface AuthProviderProps {
  children: ReactNode;
}

function restoreInitialAuthState(): AuthState {
  const restored = restorePortalSession();
  if (!restored) return { token: null, user: null, tenantId: null };

  // La restauración ocurre antes de renderizar las rutas protegidas. Así sus queries iniciales
  // ya salen autenticadas y el guard no redirige transitoriamente a /login.
  tokenStorage.set(restored.token);
  setAuthToken(restored.token);
  setTenantHeader(restored.tenantId);
  return {
    token: restored.token,
    user: restored.user,
    tenantId: restored.tenantId,
  };
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [state, setState] = useState<AuthState>(restoreInitialAuthState);

  const logout = useCallback((): void => {
    clearPortalSession();
    tokenStorage.clear();
    clearAuth();
    resetIdentity();
    setState({ token: null, user: null, tenantId: null });
  }, []);

  // Conecto el axios interceptor (401 → logout) con este context.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
    });
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [logout]);

  const login = useCallback(
    async (email: string, password: string, tenantId?: string): Promise<UserInfoResponse> => {
      const resp = await authApi.login({ email, password, tenantId: tenantId ?? null });
      // Configuro el cliente HTTP ANTES de setear el state — así cualquier request que dispare
      // un effect downstream ya sale con auth headers correctos.
      tokenStorage.set(resp.accessToken);
      setAuthToken(resp.accessToken);
      setTenantHeader(resp.user.tenantId);
      persistPortalSession(resp.accessToken, resp.user, resp.expiresIn);
      identifyTenant(resp.user.tenantId, { role: resp.user.role });
      trackEvent('user_logged_in', { provider: 'password', tenantId: resp.user.tenantId });
      setState({ token: resp.accessToken, user: resp.user, tenantId: resp.user.tenantId });
      return resp.user;
    },
    [],
  );

  const signInWithProvider = useCallback(
    async (
      provider: OAuthProvider,
      idToken: string,
      persona?: AccountPersona,
    ): Promise<OAuthSignInOutcome> => {
      const resp = await authApi.oauthSignIn(provider, idToken, persona);
      if (import.meta.env.DEV) {
        console.info('[tilde-auth] OAuth response', {
          hasAccessToken: Boolean(resp.accessToken),
          accessTokenLength: resp.accessToken.length,
          tenantId: resp.user.tenantId,
        });
      }
      tokenStorage.set(resp.accessToken);
      setAuthToken(resp.accessToken);
      setTenantHeader(resp.user.tenantId);
      persistPortalSession(resp.accessToken, resp.user, resp.expiresIn);
      if (import.meta.env.DEV) {
        console.info('[tilde-auth] session stored', {
          hasToken: Boolean(tokenStorage.get()),
          tokenLength: tokenStorage.get()?.length ?? 0,
          tenantId: resp.user.tenantId,
        });
      }
      identifyTenant(resp.user.tenantId, { role: resp.user.role });
      trackEvent('user_logged_in', {
        provider,
        tenantId: resp.user.tenantId,
        isNewUser: resp.isNewUser,
      });
      setState({ token: resp.accessToken, user: resp.user, tenantId: resp.user.tenantId });
      return { user: resp.user, isNewUser: resp.isNewUser };
    },
    [],
  );

  const redeemMagicLink = useCallback(
    async (token: string): Promise<UserInfoResponse> => {
      const resp = await authApi.redeemMagicLink({ token });
      tokenStorage.set(resp.accessToken);
      setAuthToken(resp.accessToken);
      setTenantHeader(resp.user.tenantId);
      persistPortalSession(resp.accessToken, resp.user, resp.expiresIn);
      setState({ token: resp.accessToken, user: resp.user, tenantId: resp.user.tenantId });
      return resp.user;
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isAuthenticated: !!state.token && !!state.user,
      login,
      signInWithProvider,
      redeemMagicLink,
      logout,
    }),
    [state, login, signInWithProvider, redeemMagicLink, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
